import {
  Banknote,
  BarChart3,
  Bell,
  BookOpen,
  Building,
  Calendar,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  FileText,
  Home,
  Image,
  List,
  MessageSquare,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
  Settings,
  ShoppingCart,
  Star,
  StarOff,
  Truck,
  Users,
  Wrench,
} from 'lucide-react'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useSolicitadoCount } from '@/features/compras/services/comprasService'
import { useNotificationCount } from '@/features/comunicacao/hooks/useNotificationCount'
// Atalhos e modal iguais aos da Navbar
import QuickTaskCreator from '@/features/tarefas/components/QuickTaskCreator'

import { useAuth } from '../hooks/useAuth'
import { supabase } from '../services/supabaseClient'

// Status conforme CHECK da tabela
type ObraStatus = 'em_andamento' | 'paralisada' | 'concluida' | string

type ObraSlim = {
  id: string
  nome: string
  status: ObraStatus
}

type BadgeCounts = {
  tarefas?: number
  diario?: number
  servicos?: number
  compras?: number
  checklists?: number
}

// --- Helpers de localStorage simples (sem depender de hooks externos)
function useLocalStorage<T>(key: string, initial: T) {
  const [state, setState] = React.useState<T>(() => {
    try {
      const raw = localStorage.getItem(key)
      return raw ? (JSON.parse(raw) as T) : initial
    } catch {
      return initial
    }
  })
  React.useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state))
    } catch {}
  }, [key, state])
  return [state, setState] as const
}

// Mapeamento dos chips (sem "Cliente" e sem "Financeiro")
const MODULES: {
  key: keyof BadgeCounts
     | 'cronograma'
     | 'tarefas'
     | 'diario'
     | 'servicos'
     | 'compras'
     | 'checklists'
     | 'contratos'
     | 'midia'
     | 'ppc';
  label: string;
  icon: React.ElementType;
  path: (obraId: string)=>string;
}[] = [
  { key: 'cronograma', label: 'Cronograma', icon: Calendar, path: (id)=>`/obras/${id}/cronograma` },
  { key: 'tarefas',    label: 'Tarefas',    icon: List,      path: (id)=>`/obras/${id}/tarefas` },
  { key: 'diario',     label: 'Diario',     icon: BookOpen,  path: (id)=>`/obras/${id}/diarios` },
  { key: 'servicos',   label: 'Servicos',   icon: Wrench,    path: (id)=>`/obras/${id}/servicos` },
  { key: 'ppc',        label: 'PPC',        icon: BarChart3, path: (id)=>`/obras/${id}/ppc` },
  { key: 'compras',    label: 'Compras',    icon: ShoppingCart, path: (id)=>`/obras/${id}/compras` },
  { key: 'checklists', label: 'Checklists', icon: CheckSquare,  path: (id)=>`/obras/${id}/checklist` },
  { key: 'contratos',  label: 'Contratos',  icon: FileText,     path: (id)=>`/obras/${id}/contratos` },
  { key: 'midia',      label: 'Midia',      icon: Image,     path: (id)=>`/obras/${id}/fotos` },
]

interface SidebarProps {
  // opcional: injetar uma função para buscar contadores por obra
  getBadgeCounts?: (obraId: string) => Promise<BadgeCounts>
}

const Sidebar: React.FC<SidebarProps> = ({ getBadgeCounts }) => {
  const navigate = useNavigate()
  const location = useLocation()
  // Extrai obraId se a rota atual for /obras/:id/...
  const currentObraId = React.useMemo(() => {
    const m = location.pathname.match(/\/obras\/([0-9a-f-]{36})/i);
    return m?.[1];
  }, [location.pathname]);

  // Estado visual
  const [collapsed, setCollapsed] = useLocalStorage<boolean>('acor:sidebar:collapsed', false)
  const [query, setQuery] = React.useState('')
  const [expandedConcluidas, setExpandedConcluidas] = useLocalStorage<boolean>('acor:sidebar:concluidas:expanded', false)
  const [expandedOutras, setExpandedOutras] = useLocalStorage<boolean>('acor:sidebar:outras:expanded', true)
  // ordem de módulos no "rail" (recolhido) — agora com 6 itens incluindo Cronograma
  const RAIL_MODULES = React.useMemo(() => {
    const order: Array<typeof MODULES[number]['key']> = ['cronograma','tarefas','diario','servicos','compras','checklists']
    return order.map(k => MODULES.find(m => m.key === k)!).filter(Boolean)
  }, [])
  const [expandedObras, setExpandedObras] = useLocalStorage<Record<string, boolean>>('acor:sidebar:obras:expanded', {})
  const [favoriteIds, setFavoriteIds] = useLocalStorage<string[]>('acor:sidebar:favorites', [])
  const [expandedCadastros, setExpandedCadastros] = useLocalStorage<boolean>('acor:sidebar:cadastros:expanded', false)
  const [showQuickTask, setShowQuickTask] = React.useState(false)

  // Dados
  const [obras, setObras] = React.useState<ObraSlim[]>([])
  const [badges, setBadges] = React.useState<Record<string, BadgeCounts>>({})
  // Badge global de Compras (mesmo hook da Navbar)
  const { count: solicitadoCount } = useSolicitadoCount()
  // Badge de notificações – agora o hook já dá { unread, mentions, total }
  const { unread, mentions, total, refetch } = useNotificationCount(currentObraId || 'ALL')
  const totalNotif = total ?? unread ?? 0
  // Usuário logado (mesma origem da Navbar)
  const { user } = useAuth()
  const displayName = user?.user_metadata?.nome || user?.email || 'Usuário'
  const displayInitial = user?.user_metadata?.nome?.charAt(0) || user?.email?.charAt(0) || 'U'
  // ✅ Admin via metadados + via BD (mesma checagem usada na Navbar)
  const adminMeta = React.useMemo(() => {
    const am = (user?.app_metadata as any) || {}
    const um = (user?.user_metadata as any) || {}
    return !!(am.is_admin || (Array.isArray(am.roles) && am.roles.includes('admin')) || um.is_admin || um.perfil === 'admin' || um.role === 'admin')
  }, [user])
  const [adminDB, setAdminDB] = React.useState(false)
  React.useEffect(() => {
    let ignore = false
    ;(async () => {
      try {
        if (!user?.id) { if (!ignore) setAdminDB(false); return }
        const { data, error } = await supabase.from('usuarios').select('role').eq('id', user.id).single()
        if (!ignore) setAdminDB(!error && data?.role === 'admin')
      } catch { if (!ignore) setAdminDB(false) }
    })()
    return () => { ignore = true }
  }, [user?.id])
  const admin = adminMeta || adminDB  // 🔒 final

  // Carrega obras (mínimo: id, nome, status) - sem pessoais
  React.useEffect(() => {
    let isMounted = true
    ;(async () => {
      try {
        const { data, error } = await supabase
          .from('obras')
          .select('id, nome, status, is_personal')
          // Exclui pessoais (inclui NULL e FALSE)
          .or('is_personal.is.null,is_personal.eq.false')
          .order('nome', { ascending: true })
        if (error) throw error
        if (!isMounted) return
        setObras((data || []) as ObraSlim[])
        // Log útil para debug: se vier vazio, pode ser RLS
        if (!data || data.length === 0) {
          console.warn('Sidebar: nenhuma obra retornada. Verifique RLS/políticas e se existem obras.')
        }
      } catch (e) {
        console.warn('Sidebar: falha ao carregar obras', e)
        if (isMounted) setObras([])
      }
    })()
    return () => { isMounted = false }
  }, [])

  // Carrega badges por obra (opcional)
  React.useEffect(() => {
    if (!getBadgeCounts) return
    let cancelled = false
    ;(async () => {
      const next: Record<string, BadgeCounts> = {}
      for (const o of obras) {
        try {
          const c = await getBadgeCounts(o.id)
          if (cancelled) return
          next[o.id] = c
        } catch {}
      }
      if (!cancelled) setBadges(next)
    })()
    return () => { cancelled = true }
  }, [getBadgeCounts, obras])

  // Helpers
  const isFav = (id: string) => favoriteIds.includes(id)
  const toggleFav = (id: string) => {
    setFavoriteIds(prev => isFav(id) ? prev.filter(x=>x!==id) : [...prev, id])
    // regra UX: favoritos sempre expandidos
    setExpandedObras(prev => ({ ...prev, [id]: true }))
  }
  const toggleExpandedObra = (id: string) => {
    setExpandedObras(prev => ({ ...prev, [id]: !prev[id] }))
  }
  const go = (href: string) => navigate(href)

  // Filtro por busca
  const filtered = obras.filter(o =>
    o.nome.toLowerCase().includes(query.toLowerCase())
  )

  // Particiona: favoritas / outras (ativas) / concluídas
  const favoritas = filtered.filter(o => isFav(o.id))
  const concluidas = filtered.filter(o => (o.status === 'concluida'))
  const outras = filtered.filter(o => !isFav(o.id) && o.status !== 'concluida')


  type ObraVariant = 'favorite' | 'other' | 'done'

  // Grupo de uma obra (linha + conteúdo) — EXPANDIDO
  const ObraBlock: React.FC<{ obra: ObraSlim; expandedDefault?: boolean; variant: ObraVariant }> = ({ obra, expandedDefault }) => {
    const expanded = expandedObras[obra.id] ?? expandedDefault ?? false
    const toggle = () => toggleExpandedObra(obra.id)
    return (
      <div className="mb-1.5">
        <div className="relative flex items-center justify-between rounded-md bg-neutral-50 dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-800 px-2 py-1">
          {/* botão de expandir ocupa só o necessário e não empurra o favorito */}
          <button onClick={toggle} className="flex-1 min-w-0 flex items-center gap-1.5 text-left">
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <span className={`truncate ${collapsed ? 'hidden' : 'block'} text-[13px] leading-5 text-neutral-900 dark:text-neutral-100`}>{obra.nome}</span>
            {/* Sem tag de status no cabeçalho (aplicado a todas as seções) */}
          </button>
          {/* favorito fixo ao final da caixa, sempre visível e sem extrapolar */}
          {!collapsed && (
          <button
            onClick={() => toggleFav(obra.id)}
            className="shrink-0 ml-2 p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-700"
            title={isFav(obra.id) ? 'Remover dos favoritos' : 'Favoritar obra'}
          >
            {isFav(obra.id) ? <Star className="h-3.5 w-3.5 text-amber-500" /> : <StarOff className="h-3.5 w-3.5 opacity-60" />}
          </button>
          )}
        </div>
        {expanded && !collapsed && (
          // ✅ Todas as seções: lista VERTICAL de módulos (compacta)
          <nav className="mt-0.5 pl-6 space-y-0.5">
            {MODULES.map(m => {
              const Icon = m.icon
              const href = m.path(obra.id)
              const b = badges[obra.id] || {}
              const badgeValue =
                m.key === 'tarefas' ? b.tarefas :
                m.key === 'diario' ? b.diario :
                m.key === 'servicos' ? b.servicos :
                m.key === 'compras' ? b.compras :
                m.key === 'checklists' ? b.checklists :
                undefined
              const active = location.pathname === href
              return (
                <button
                  key={m.key + obra.id}
                  onClick={() => go(href)}
                  className={`w-full flex items-center justify-between rounded-md px-2 py-1 text-[13px] leading-5
                    ${active ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200' : 'hover:bg-neutral-50 dark:hover:bg-neutral-900/50 text-neutral-800 dark:text-neutral-200'}`}
                  title={`${m.label} • ${obra.nome}`}
                >
                  <span className="flex items-center gap-1.5">
                    <Icon className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-200" />
                    <span className="truncate text-[13px] text-neutral-800 dark:text-neutral-200">{m.label}</span>
                  </span>
                  {badgeValue ? (
                    <span className="ml-2 rounded-md bg-neutral-200 dark:bg-neutral-800 px-1.5 text-[10px] leading-4 text-neutral-800 dark:text-neutral-200">{badgeValue}</span>
                  ) : null}
                </button>
              )
            })}
          </nav>
        )}
      </div>
    )
  }

  // Item de obra no modo RECOLHIDO (rail) — módulos empilhados
  const getInitials = (nome?: string) => {
    if (!nome) return 'OB'
    const parts = nome.trim().split(/\s+/).slice(0, 2)
    return parts.map(p => p[0]?.toUpperCase()).join('').substring(0, 2) || 'OB'
  }
  const ObraRailItem: React.FC<{ obra: ObraSlim }> = ({ obra }) => {
    return (
      <div className="mb-2 flex flex-col items-center">
        <div className="w-8 h-8 rounded-full bg-neutral-200 dark:bg-neutral-800 text-[11px] leading-[32px] text-neutral-800 dark:text-neutral-100 font-medium flex items-center justify-center" title={obra.nome}>
          {getInitials(obra.nome)}
        </div>
        <div className="mt-1 w-full flex flex-col items-center gap-1">
          {RAIL_MODULES.map(m => {
            const Icon = m.icon
            const href = m.path(obra.id)
            return (
              <button
                key={m.key + obra.id}
                onClick={() => go(href)}
                className="w-full rounded px-1 py-1 hover:bg-neutral-100 dark:hover:bg-neutral-900/60 flex items-center justify-center"
                title={`${obra.nome} • ${m.label}`}
              >
                <Icon className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-200" />
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <aside className={`h-[100dvh] shrink-0 border-r border-neutral-200 dark:border-neutral-800 bg-white dark:bg-gray-900
      ${collapsed ? 'w-[72px]' : 'w-[240px]'} sticky top-0 flex flex-col`}>
      {/* Header */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-neutral-200 dark:border-neutral-800">
        {/* Buscar obra à esquerda */}
        {!collapsed ? (
          <div className="flex items-center gap-1.5 flex-1 bg-neutral-50 dark:bg-neutral-900 rounded px-2 py-1">
            <Search className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />
            <input
              value={query}
              onChange={e=>setQuery(e.target.value)}
              placeholder="Buscar obra..."
              className="bg-transparent outline-none text-[13px] leading-5 w-full placeholder:text-neutral-400 dark:placeholder:text-neutral-500 text-neutral-800 dark:text-neutral-200"
            />
          </div>
        ) : (
          <div className="flex-1" />
        )}
        {/* Botão de recolher à direita */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800"
          title={collapsed ? 'Expandir barra' : 'Recolher barra'}
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4 text-neutral-700 dark:text-neutral-300" /> : <PanelLeftClose className="h-4 w-4 text-neutral-700 dark:text-neutral-300" />}
        </button>
      </div>

      {/* Atalhos globais (acima de Favoritas) — vertical, com destaque em "Nova Tarefa" */}
      <div className="px-2 pt-1.5">
        {/* Nova Tarefa (destaque leve) */}
        <button
          onClick={() => setShowQuickTask(true)}
          className={`w-full mb-1.5 flex items-center gap-1.5 rounded-md border px-2.5 py-1.5
                     ${collapsed ? 'justify-center' : 'justify-start'}
                     border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/40`}
          title="Nova Tarefa"
        >
          <Plus className="h-3.5 w-3.5 text-blue-700 dark:text-blue-200" />
          {!collapsed && <span className="text-[13px] leading-5 font-medium">Nova Tarefa</span>}
        </button>
        {/* Home (/obras) */}
        <button
          onClick={() => go('/obras')}
          className={`w-full mb-1.5 flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-800
                     ${collapsed ? 'justify-center' : 'justify-start'}`}
          title="Home"
        >
          <Home className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-200" />
          {!collapsed && <span className="text-[13px] leading-5 text-neutral-800 dark:text-neutral-200">Home</span>}
        </button>
        {/* Notificações (agora logo abaixo de Nova Tarefa) */}
        <button
          onClick={() => {
            refetch()
            go('/notificacoes')
          }}
          className={`w-full mb-1.5 relative flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-800
                     ${collapsed ? 'justify-center' : 'justify-start'}`}
          title="Notificações"
        >
          <Bell className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-200" />
          {!collapsed && <span className="text-[13px] leading-5 text-neutral-800 dark:text-neutral-200">Notificações</span>}
          {/* 🔴 Menções (vermelho) */}
          {mentions > 0 && (
            <span className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/3 min-w-[16px] h-[16px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
              {mentions > 99 ? '99+' : mentions}
            </span>
          )}
          {/* 🔵 Outras (azul) */}
          {totalNotif > mentions && (
            <span className="absolute -bottom-1 -right-1 min-w-[14px] h-3.5 bg-blue-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1">
              {totalNotif - mentions > 99 ? '99+' : totalNotif - mentions}
            </span>
          )}
        </button>
        {/* Minhas Tarefas */}
        <button
          onClick={() => go('/minhas-tarefas')}
          className={`w-full mb-1.5 flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-800
                     ${collapsed ? 'justify-center' : 'justify-start'}`}
          title="Minhas Tarefas"
        >
          <CheckSquare className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-200" />
          {!collapsed && <span className="text-[13px] leading-5 text-neutral-800 dark:text-neutral-200">Minhas Tarefas</span>}
        </button>

        {/* Assistente — agora abre página dedicada */}
        <button
          onClick={() => go(`/assistente${currentObraId ? `?obraId=${currentObraId}` : ''}`)}
          className={`w-full mb-1.5 flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-800
                     ${collapsed ? 'justify-center' : 'justify-start'}`}
          title="Assistente"
          aria-label="Abrir Assistente"
        >
          <MessageSquare className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-200" />
          {!collapsed && <span className="text-[13px] leading-5 text-neutral-800 dark:text-neutral-200">Assistente</span>}
        </button>
        {/* Compras (painel) */}
        <button
          onClick={() => go('/compras')}
          className={`w-full mb-1.5 relative flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-800
                     ${collapsed ? 'justify-center' : 'justify-start'}`}
          title="Compras"
        >
          <Truck className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-200" />
          {!collapsed && <span className="text-[13px] leading-5 text-neutral-800 dark:text-neutral-200">Compras</span>}
          {solicitadoCount > 0 && (
            <span className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/3 min-w-[16px] h-[16px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
              {solicitadoCount > 99 ? '99+' : solicitadoCount}
            </span>
          )}
        </button>

        {/* Financeiro (painel) */}
        <button
          onClick={() => go('/financeiro')}
          className={`w-full mb-1.5 relative flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-800
                     ${collapsed ? 'justify-center' : 'justify-start'}`}
          title="Financeiro"
        >
          <Banknote className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-200" />
          {!collapsed && <span className="text-[13px] leading-5 text-neutral-800 dark:text-neutral-200">Financeiro</span>}
        </button>

        {/* RH (sistema externo) */}
        <button
          onClick={async () => {
            try {
              const { data: { session } } = await supabase.auth.getSession()
              if (session?.access_token && session?.refresh_token) {
                // Redireciona com tokens para o sistema RH
                const url = new URL('https://acor.eng.br/rh/')
                url.searchParams.set('access_token', session.access_token)
                url.searchParams.set('refresh_token', session.refresh_token)
                window.location.href = url.toString()
              } else {
                console.error('Sessão não encontrada')
              }
            } catch (error) {
              console.error('Erro ao redirecionar para RH:', error)
            }
          }}
          className={`w-full relative flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-800
                     ${collapsed ? 'justify-center' : 'justify-start'}`}
          title="RH"
        >
          <Users className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-200" />
          {!collapsed && <span className="text-[13px] leading-5 text-neutral-800 dark:text-neutral-200">RH</span>}
        </button>
      </div>

      {/* Conteúdo */}
      {!collapsed ? (
      <div className="px-2 py-2 overflow-y-auto flex-1 min-h-0 overscroll-contain pb-24 webkit-scrollbar-hide">
        {/* Favoritas */}
        {!collapsed && <div className="text-[11px] uppercase tracking-wide text-neutral-600 dark:text-neutral-400 mb-1.5 mt-1.5">Favoritas</div>}
        {favoritas.map(o => (
          <ObraBlock key={o.id} obra={o} expandedDefault variant="favorite" />
        ))}

        {/* Outras (seção com recolher próprio) */}
        {!collapsed && (
          <button
            onClick={() => setExpandedOutras(!expandedOutras)}
            className="mt-3 mb-1.5 flex items-center gap-1.5 w-full text-left text-[11px] uppercase tracking-wide text-neutral-600 dark:text-neutral-400 hover:underline"
            title={expandedOutras ? 'Recolher outras obras' : 'Expandir outras obras'}
          >
            {expandedOutras ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <span>Outras obras</span>
          </button>
        )}
        {expandedOutras && outras.map(o => (
          <ObraBlock key={o.id} obra={o} expandedDefault={false} variant="other" />
        ))}

        {/* Concluídas (seção própria, recolhida por padrão) */}
        <div className="mt-3">
          <button
            onClick={() => setExpandedConcluidas(!expandedConcluidas)}
            className="flex items-center gap-1.5 w-full text-left text-[13px] leading-5 text-neutral-700 dark:text-neutral-300 hover:underline"
          >
            {expandedConcluidas ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <span className={`${collapsed ? 'hidden' : 'block'}`}>Obras concluídas</span>
          </button>
          {expandedConcluidas && concluidas.map(o => (
            <ObraBlock key={o.id} obra={o} expandedDefault={false} variant="done" />
          ))}
        </div>

        {/* Rodapé — Menus adicionais + Meu Perfil (agora por último) */}
        {!collapsed && (
          <div className="mt-4 border-t border-neutral-200 dark:border-neutral-800 pt-2">
            {/* Menus do perfil (como na Navbar) */}
            <div className="mt-2 space-y-1">
              {/* Dashboards */}
              <button
                onClick={()=>go('/dashboards')}
                className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200"
                title="Dashboards"
              >
                <BarChart3 className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />
                <span>Dashboards</span>
              </button>
              
              {/* Servicos (geral) - acesso amplo, fica ACIMA de Nova Obra */}
              <button
                onClick={()=>go('/servicos/geral')}
                className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200"
                title="Serviços (geral)"
              >
                <Wrench className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />
                <span>Serviços (geral)</span>
              </button>

              {/* Orçamentos */}
              <button
                onClick={()=>go('/orcamentos')}
                className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200"
                title="Orçamentos"
              >
                <FileText className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />
                <span>Orçamentos</span>
              </button>

              {/* Cadastros (seção colapsável) */}
              <div className="mt-2">
                <button
                  onClick={() => setExpandedCadastros(!expandedCadastros)}
                  className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200"
                  title={expandedCadastros ? 'Recolher Cadastros' : 'Expandir Cadastros'}
                >
                  {expandedCadastros ? <ChevronDown className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" /> : <ChevronRight className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />}
                  <Settings className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />
                  <span>Cadastros</span>
                </button>
                {expandedCadastros && (
                  <div className="ml-4 mt-1 space-y-1 border-l border-neutral-200 dark:border-neutral-700 pl-2">
                    {/* Admin-only: Nova Obra */}
                    {admin && (
                      <button
                        onClick={()=>go('/obras/nova')}
                        className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200"
                        title="Nova Obra"
                      >
                        <Building className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />
                        <span>Nova Obra</span>
                      </button>
                    )}
                    {/* Admin-only: Gerenciar Usuários */}
                    {admin && (
                      <button
                        onClick={()=>go('/usuarios')}
                        className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200"
                        title="Gerenciar Usuários"
                      >
                        <Users className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />
                        <span>Gerenciar Usuários</span>
                      </button>
                    )}
                    <button
                      onClick={()=>go('/empresas')}
                      className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-800 dark:text-neutral-200"
                      title="Empresas"
                    >
                      <Building className="h-3.5 w-3.5 opacity-80 text-neutral-700 dark:text-neutral-300" />
                      <span>Empresas</span>
                    </button>
                    <button
                      onClick={()=>go('/configuracoes/materiais-catalogo')}
                      className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-800 dark:text-neutral-200"
                      title="Materiais Catálogo"
                    >
                      <Package className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />
                      <span>Materiais Catálogo</span>
                    </button>
                    <button
                      onClick={()=>go('/checklists/templates')}
                      className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-800 dark:text-neutral-200"
                      title="Editar Itens Checklist"
                    >
                      <CheckSquare className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />
                      <span>Itens Checklist</span>
                    </button>
                    <button
                      onClick={()=>go('/cadastros/catalogo-itens')}
                      className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-800 dark:text-neutral-200"
                      title="Catálogo de Itens de Orçamento"
                    >
                      <FileText className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />
                      <span>Catálogo Orçamento</span>
                    </button>
                    <button
                      onClick={()=>go('/cadastros/ppc-categorias')}
                      className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-800 dark:text-neutral-200"
                      title="Categorias PPC"
                    >
                      <BarChart3 className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />
                      <span>Categorias PPC</span>
                    </button>
                  </div>
                )}
              </div>
              {/* Meu Perfil — agora por último */}
              <button
                onClick={()=>go('/configuracoes')}
                className="w-full mt-1.5 flex items-center gap-2.5 rounded-md border px-2.5 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60"
                title="Meu Perfil"
              >
                <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-[12px]">
                  {displayInitial}
                </div>
                <div className="flex-1 text-left">
                  <div className="text-[13px] leading-5 font-medium text-neutral-900 dark:text-neutral-100">Meu Perfil</div>
                  <div className="text-[11px] text-neutral-600 dark:text-neutral-300 truncate">{displayName}</div>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
      ) : (
        /* ---------- Rail recolhido: SOMENTE FAVORITAS ---------- */
        <div className="px-1 py-2 overflow-y-auto flex-1 min-h-0 overscroll-contain pb-24 webkit-scrollbar-hide">
          <div className="text-[10px] uppercase tracking-wide text-neutral-600 dark:text-neutral-400 mb-2 text-center">Favoritas</div>
          <div className="grid grid-cols-1 gap-2">
            {favoritas.map(o => <ObraRailItem key={o.id} obra={o} />)}
          </div>
        </div>
      )}
      {/* Quick Task Creator Modal (igual à Navbar) */}
      <QuickTaskCreator
        isOpen={showQuickTask}
        onClose={() => setShowQuickTask(false)}
        onSuccess={() => setShowQuickTask(false)}
      />
    </aside>
  )
}

export default Sidebar
